import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { X } from 'lucide-react';

import useAtsCandidatesApi from './config';
import CandidateDetailDrawer from './CandidateDetailDrawer';
import { parseTableParams } from '../../utils/Utils';
import DataTable from '../../components/ui/DataTable';
import OptionsMenu from '../../components/ui/OptionsMenu';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import GenericFormDrawer from '../../components/GenericFormDrawer';
import { useAuth } from '../../context/AuthContext';
import Button from '../../components/ui/Button';
import SelectionBar, { selectionColumn } from '../ats-candidate-pools/components/SelectionBar';
import AddToPoolDrawer from '../ats-candidate-pools/components/AddToPoolDrawer';

const Container = styled.div``;

const RowActions = styled.div` display: flex; align-items: center; justify-content: flex-end; gap: 6px; `;

// Uygulanan süzgecin AFİŞİ. Kaynağı `filterList` DEĞİL — DataTable o prop'u
// yutuyor (mui-datatables@4.3.0 okumuyor); afiş uygulanan süzgeçten türer.
// Mükerrer aday reddedildiğinde liste otomatik olarak o e-postaya süzülür ve
// kullanıcı "neden kaydedilmedi" sorusuyla baş başa kalmaz.
const FilterChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.primary.main};
  background: rgba(2, 145, 239, 0.1);
`;

const ChipClose = styled.button`
  display: inline-flex;
  border: none;
  background: none;
  padding: 0;
  cursor: pointer;
  color: inherit;
`;

const BASE_QUERY = { pageSize: 10, pageNumber: 1 };

export default function AtsCandidatesPage() {
  const { t } = useTranslation();
  const history = useHistory();
  const { canWrite } = useAuth();
  const writable = canWrite ? canWrite('AT') : false;

  const [queryParams, setQueryParams] = useState(BASE_QUERY);
  const [filterList, setFilterList] = useState([]);
  // Mükerrer e-posta reddinden sonra listeye uygulanan süzgeç (afişin kaynağı).
  const [duplicateEmail, setDuplicateEmail] = useState(null);

  const [formDrawer, setFormDrawer] = useState({ open: false, row: null });
  const [applyDrawer, setApplyDrawer] = useState({ open: false, row: null });
  const [detailDrawer, setDetailDrawer] = useState({ open: false, row: null });

  const [deleteDialog, setDeleteDialog] = useState({ isOpen: false, id: null, name: '' });
  const [deleting, setDeleting] = useState(false);

  // Toplu "Aday Havuzuna Ekle" (SPEC F2, yalnız AT.Write). Seçim SAYFA kapsamlıdır.
  const [selectedIds, setSelectedIds] = useState([]);
  const [poolDrawerOpen, setPoolDrawerOpen] = useState(false);
  // Tabloya verilen GÜNCEL kolonlar: seçim kolonu başa eklendiğinde süzgeç
  // indeksleri kayar — parseTableParams taban kolonlarla DEĞİL bunlarla eşlemeli.
  const columnsRef = useRef([]);

  const {
    fetching, data, pagination, columns: baseColumns,
    objectSchema, applicationSchema, create, update, createApplication, remove,
  } = useAtsCandidatesApi(queryParams);

  const goToApplications = useCallback((row) => {
    if (!row?.id) return;
    history.push({
      pathname: '/ats/applications',
      search: `?candidateId=${row.id}`,
      state: { candidateFilter: { id: row.id, name: row.fullName } },
    });
  }, [history]);

  // Hızlı bakış çekmecesi (satır menüsü "Detayı Görüntüle").
  const openDetail = useCallback((row) => {
    if (!row?.id) return;
    setDetailDrawer({ open: true, row });
  }, []);

  // Satır tıklaması TAM PROFİL sayfasına gider (notlar/etiketler/belgeler/rıza orada).
  const openProfilePage = useCallback((row) => {
    if (!row?.id) return;
    history.push(`/ats/candidates/${row.id}`);
  }, [history]);

  useEffect(() => { setSelectedIds([]); }, [data, queryParams]);

  const toggleSelect = useCallback((id) => {
    setSelectedIds((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]));
  }, []);

  const handleDataStateChange = useCallback((action, tableState) => {
    setFilterList(tableState.filterList);
    const params = parseTableParams(tableState, columnsRef.current);
    setQueryParams(params);
    // Kullanıcı kendi süzgecini kurdu — mükerrer afişi artık uygulanan süzgeci
    // anlatmıyor, kaldırılmalı (afiş ile liste ASLA ayrışmamalı).
    setDuplicateEmail(null);
  }, []);

  const handleFilterConfirm = useCallback((newFilterList) => {
    setFilterList(newFilterList);
  }, []);

  const clearDuplicateFilter = useCallback(() => {
    setDuplicateEmail(null);
    setQueryParams(BASE_QUERY);
  }, []);

  const handleNewButtonClick = useCallback(() => {
    setFormDrawer({ open: true, row: null });
  }, []);

  const handleEdit = useCallback((row) => {
    setFormDrawer({ open: true, row });
  }, []);

  // GenericFormDrawer `false` dönerse çekmeceyi AÇIK bırakır (yazılan veri
  // kaybolmaz). Mükerrer e-postada ise çekmece bilerek KAPANIR: kayıt zaten
  // var, kullanıcının yazdığına ihtiyaç yok — onun yerine liste o e-postaya
  // süzülür ve var olan aday ekrana gelir.
  const handleSave = useCallback(async (formData) => {
    const result = formDrawer.row?.id
      ? await update(formDrawer.row.id, formData)
      : await create(formData);
    if (result?.ok) {
      setFormDrawer({ open: false, row: null });
      return true;
    }
    if (result?.duplicateEmail) {
      setFormDrawer({ open: false, row: null });
      setDuplicateEmail(result.duplicateEmail);
      setQueryParams({ ...BASE_QUERY, 'email[like]': result.duplicateEmail });
      return true;
    }
    return false;
  }, [formDrawer, create, update]);

  const handleApplySave = useCallback(async (formData) => {
    const ok = await createApplication(applyDrawer.row?.id, formData);
    if (ok) setApplyDrawer({ open: false, row: null });
    return ok;
  }, [applyDrawer, createApplication]);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteDialog.id) return;
    setDeleting(true);
    await remove(deleteDialog.id);
    setDeleteDialog({ isOpen: false, id: null, name: '' });
    setDeleting(false);
  }, [deleteDialog, remove]);

  const buildActions = useCallback((row) => {
    if (!row) return [];
    const actions = [
      {
        type: 'details',
        label: t('ats.candidate.viewDetail'),
        onClick: () => openDetail(row),
      },
      {
        type: 'open',
        label: t('ats.candidate.openFullProfile'),
        onClick: () => openProfilePage(row),
      },
      {
        type: 'view_applications',
        label: t('ats.candidate.viewApplications'),
        onClick: () => goToApplications(row),
      },
    ];
    if (writable) {
      actions.push({
        type: 'edit',
        label: t('ats.candidate.edit'),
        onClick: () => handleEdit(row),
      });
      actions.push({
        type: 'apply',
        label: t('ats.candidate.createApplication'),
        onClick: () => setApplyDrawer({ open: true, row }),
      });
    }
    if (row.employeeId) {
      actions.push({
        type: 'open',
        label: t('ats.candidate.openPersonnelFile'),
        onClick: () => history.push(`/personnel-files/${row.employeeId}`),
      });
    }
    if (writable) {
      actions.push({ type: 'divider' });
      actions.push({
        type: 'delete',
        label: t('common.delete'),
        onClick: () => setDeleteDialog({ isOpen: true, id: row.id, name: row.fullName }),
      });
    }
    return actions;
  }, [t, writable, goToApplications, openDetail, openProfilePage, handleEdit, history]);

  const columns = useMemo(() => {
    return [
      ...(writable ? [selectionColumn({ data, selectedIds, onToggle: toggleSelect })] : []),
      ...baseColumns,
      {
        name: 'actions',
        label: ' ',
        options: {
          filter: false, sort: false, download: false, print: false,
          customBodyRender: (_, tableMeta) => {
            const row = data[tableMeta.rowIndex];
            const actions = buildActions(row);
            if (actions.length === 0) return null;
            // Aksiyon hücresindeki tıklama satır tıklamasına YÜKSELMEZ —
            // aksi halde ⋮ her basışta detay çekmecesi de açılırdı.
            return (
              <RowActions onClick={(e) => e.stopPropagation()}>
                <OptionsMenu actions={actions} row={row} />
              </RowActions>
            );
          },
        },
      },
    ];
  }, [baseColumns, data, buildActions, writable, selectedIds, toggleSelect]);

  columnsRef.current = columns;

  return (
    <Container>
      <GenericFormDrawer
        open={formDrawer.open}
        onClose={() => setFormDrawer({ open: false, row: null })}
        title={formDrawer.row?.id ? t('ats.candidate.edit') : t('ats.candidate.new')}
        schema={objectSchema}
        initialValues={formDrawer.row}
        onSave={handleSave}
      />

      <GenericFormDrawer
        open={applyDrawer.open}
        onClose={() => setApplyDrawer({ open: false, row: null })}
        title={t('ats.candidate.createApplication')}
        schema={applicationSchema}
        initialValues={null}
        onSave={handleApplySave}
      />

      <CandidateDetailDrawer
        open={detailDrawer.open}
        row={detailDrawer.row}
        onClose={() => setDetailDrawer({ open: false, row: null })}
        onOpenFullProfile={(row) => {
          setDetailDrawer({ open: false, row: null });
          openProfilePage(row);
        }}
        actions={writable && detailDrawer.row ? [{
          action: 'edit',
          label: t('ats.candidate.edit'),
          onClick: () => {
            const row = detailDrawer.row;
            setDetailDrawer({ open: false, row: null });
            handleEdit(row);
          },
        }] : []}
      />

      {writable && (
        <AddToPoolDrawer
          open={poolDrawerOpen}
          onClose={() => setPoolDrawerOpen(false)}
          candidateIds={selectedIds}
          onAdded={() => setSelectedIds([])}
        />
      )}

      {writable && (
        <SelectionBar count={selectedIds.length} onClear={() => setSelectedIds([])}>
          <Button type="button" data-role="candidates-bulk-add-to-pool" onClick={() => setPoolDrawerOpen(true)}>
            {t('ats.candidatePool.addToPool')}
          </Button>
        </SelectionBar>
      )}

      <ConfirmDialog
        isOpen={deleteDialog.isOpen}
        title={t('common.delete')}
        message={t('ats.candidate.deleteConfirm', { name: deleteDialog.name })}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteDialog({ isOpen: false, id: null, name: '' })}
        loading={deleting}
      />

      {useMemo(() => (
        <DataTable
          title={t('ats.candidate.pageTitle')}
          fetching={fetching}
          columns={columns}
          data={data}
          totalRowCount={pagination.totalRowCount}
          pageIndex={pagination.pageIndex}
          pageSize={pagination.pageSize}
          canCreateNewRecord={writable}
          newButtonLabel={t('ats.candidate.new')}
          onNewButtonClick={handleNewButtonClick}
          toolbarActions={duplicateEmail ? (
            <FilterChip>
              {t('ats.candidate.duplicateFilterChip', { email: duplicateEmail })}
              <ChipClose
                type="button"
                onClick={clearDuplicateFilter}
                title={t('ats.candidate.clearDuplicateFilter')}
                aria-label={t('ats.candidate.clearDuplicateFilter')}
              >
                <X size={14} />
              </ChipClose>
            </FilterChip>
          ) : null}
          filterList={filterList}
          onFilterConfirm={handleFilterConfirm}
          onTableChanged={handleDataStateChange}
          onRowClick={openProfilePage}
        />
      ), [
        t, fetching, columns, data, pagination, writable, handleNewButtonClick,
        duplicateEmail, clearDuplicateFilter,
        filterList, handleFilterConfirm, handleDataStateChange, openProfilePage,
      ])}
    </Container>
  );
}
